import { isNil, omit, pickBy } from 'lodash-es'

import type { LoadTemplateDTO, TemplateLocation } from '@fv/models'

import type { ApiQuoteRequestDTO } from '@/types'

import { billToMapper } from '../billTo/billToFuncs'
import { optionalFormBillTo } from '../booking/bookingPayloadHelpers'
import { buildFormCommodity } from '../commodities/commodityUtils'
import type { FormCommodity } from '../commodities/FormCommodity'
import { templateItemToLoadItem } from '../settings/shipment-templates/utils'
import type { ShipmentLocationFormModel } from '../shipment-location-form/types'

export const loadTemplateToFormCommodities = (
  template: LoadTemplateDTO,
  dropSequence: number,
): FormCommodity[] =>
  template.items?.map(item =>
    buildFormCommodity({
      item: templateItemToLoadItem(item, dropSequence),
      dropSequence,
    }),
  ) ?? []

export const loadTemplateToQuoteRequestFields = (
  template: LoadTemplateDTO | null,
): Pick<
  ApiQuoteRequestDTO,
  'billTo' | 'brandId' | 'glCode' | 'tags' | 'templateId'
> => {
  if (!template) return {}

  const billTo = optionalFormBillTo(billToMapper.dtoToForm(template.billTo))
  const { brandId, glCode, tags } = template

  return {
    templateId: template._id,
    ...(billTo && { billTo }),
    ...(glCode && { glCode }),
    ...(brandId && { brandId }),
    ...(tags?.length && { tags }),
  }
}

type TemplateStopType = 'origin' | 'destination'

const updateTemplateStops = (
  stops: ShipmentLocationFormModel[],
  template: LoadTemplateDTO,
  update: (
    stop: ShipmentLocationFormModel,
    type: TemplateStopType,
  ) => ShipmentLocationFormModel,
) =>
  stops.map((stop, ix) => {
    if (ix === 0 && template.origin) return update(stop, 'origin')
    if (ix === stops.length - 1 && template.destination) {
      return update(stop, 'destination')
    }
    return stop
  })

export const applyTemplateToStops = (
  stops: ShipmentLocationFormModel[],
  template: LoadTemplateDTO,
) =>
  updateTemplateStops(stops, template, (stop, type) =>
    mergeTemplateLocation(stop, template[type]),
  )

export const clearTemplateFromStops = (
  stops: ShipmentLocationFormModel[],
  template: LoadTemplateDTO,
  buildDefaultStop: (
    stop: ShipmentLocationFormModel,
    type: TemplateStopType,
  ) => ShipmentLocationFormModel,
) =>
  updateTemplateStops(stops, template, (stop, type) =>
    resetTemplateLocation(stop, buildDefaultStop(stop, type)),
  )

const hasTemplateValue = (value: unknown) =>
  !isNil(value) && value !== '' && !(Array.isArray(value) && !value.length)

export const mergeTemplateLocation = (
  existing: ShipmentLocationFormModel,
  template?: TemplateLocation,
): ShipmentLocationFormModel => {
  if (!template) return existing

  const { shares, ...fields } = template
  const templateFields = pickBy(
    omit(fields, 'type', 'sequence', 'stopType', 'stopDateType'),
    hasTemplateValue,
  ) as Partial<ShipmentLocationFormModel>

  return {
    ...existing,
    ...templateFields,
    ...(shares?.length && { copyBOL: true, shares: shares.join(', ') }),
  }
}

export const resetTemplateLocation = (
  existing: ShipmentLocationFormModel,
  defaults: ShipmentLocationFormModel,
): ShipmentLocationFormModel => ({
  ...defaults,
  type: existing.type,
  sequence: existing.sequence,
  stopType: existing.stopType,
  stopDate: existing.stopDate,
  stopDateType: existing.stopDateType,
  refNum: existing.refNum,
})
